import { CreateRoleSchema, type CreateRoleRequest } from '@aysar/contracts';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { type ModalComponentProps } from '@/shared/components/Modals/modal-registry/modal-registry.types';
import { DynamicFormModal } from '@/shared/components/dynamic-form/DynamicFormModal/DynamicFormModal';
import { useToast } from '@/shared/hooks/useToast/useToast';

import { buildRoleFormSections } from '../../config/role-form.config';
import {
  useCreateRoleMutation,
  useRoleDetailQuery,
  useUpdateRoleMutation,
} from '../../services/roles/roles.api';

/**
 * Creating and editing a role — the P9 proof that the form kit carries a real,
 * translatable entity end to end.
 *
 * Registered in the modal registry rather than rendered by the list, so it opens
 * from a TYPE STRING (`admins.role-form`) and any surface can open it without
 * importing it (W2). That is also why it takes only a `roleId` and loads the
 * detail **itself**: `ModalData` is serializable-only, and a modal that was
 * handed the record would go stale the moment the list refetched underneath it.
 *
 * It validates against `CreateRoleSchema` in BOTH modes, even though editing
 * sends `UpdateRoleSchema`. The update contract makes every field optional so a
 * caller can PATCH only the permissions; the form, however, always has every
 * field on screen, so validating against the looser shape would let an operator
 * blank the English name and save it. The stricter schema describes what this
 * form actually collects, and the request is narrowed on the way out.
 *
 * The dialog closes only after the mutation resolves — a modal that dismissed
 * itself on submit would take the server's error off screen with it.
 */
export function RoleFormModal({ open, onCancel, modalData }: ModalComponentProps) {
  const { t } = useTranslation('admins');
  const toast = useToast();
  const [createRole] = useCreateRoleMutation();
  const [updateRole] = useUpdateRoleMutation();

  const roleId = typeof modalData?.['roleId'] === 'string' ? modalData['roleId'] : null;
  const { data: role, isLoading } = useRoleDetailQuery(roleId ?? '', { skip: roleId === null });

  const isEditing = roleId !== null;
  const sections = useMemo(() => buildRoleFormSections(t, isEditing), [t, isEditing]);

  async function submit(values: CreateRoleRequest): Promise<void> {
    if (roleId === null) {
      await createRole(values).unwrap();
      toast.success(t('roles.form.created'));
    } else {
      // `key` is deliberately absent: the api's `.strict()` update schema
      // rejects it, because a role key is immutable.
      await updateRole({
        id: roleId,
        body: { translations: values.translations, permissions: values.permissions },
      }).unwrap();
      toast.success(t('roles.form.updated'));
    }

    onCancel();
  }

  return (
    <DynamicFormModal<CreateRoleRequest>
      isOpen={open}
      onOpenChange={(next) => {
        if (!next) {
          onCancel();
        }
      }}
      size="lg"
      // Holds the form's MOUNT until the record is here: React Hook Form reads
      // `defaultValues` once, so a form mounted against nothing stays blank when
      // the record lands and saving would wipe it.
      isLoading={isEditing && isLoading}
      title={
        isEditing && role !== undefined
          ? t('roles.form.editTitle', { name: role.name })
          : t('roles.form.createTitle')
      }
      sections={sections}
      schema={CreateRoleSchema}
      initialValues={role ?? { permissions: [] }}
      onSubmit={submit}
    />
  );
}
